import { call } from '@advanza/api'
import { Checkbox, InputField } from '@advanza/input'
import { Button, Divider } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { invalidateNotes } from 'actions/notes'
import { fetchProvider } from 'actions/providers'
import { useModalToast } from 'components/misc/Toaster'
import { useAppDispatch } from 'hooks/hooks'
import { useState } from 'react'
import { getRejectionMessage } from 'utils/getRejectionMessage'

const COMMENT_MAX_CHARACTERS = 255

interface Props {
    providerId: number
}

const AddLeadBoosterPromoLeadsModal = NiceModal.create(({ providerId }: Props) => {
    const dispatch = useAppDispatch()
    const modal = useModal()
    const [nrPromoLeads, setNrPromoLeads] = useState<null | number>(1)
    const [comment, setComment] = useState('')
    const [force, setForce] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const { addModalToast } = useModalToast()

    const closeModal = () => {
        modal.resolve()
        modal.hide()
    }

    async function onClickSave() {
        if (!nrPromoLeads || nrPromoLeads < 1) {
            setError('Number of boosted leads is required and must be at least 1')
            return
        }

        setError(null)
        setIsSaving(true)

        try {
            await call('office/providers/add-lead-booster-promo-leads', {
                json: {
                    nr_promo_leads: nrPromoLeads,
                    comment,
                    service_provider_id: providerId,
                    force,
                },
            })
            dispatch(fetchProvider(providerId, true))
            dispatch(invalidateNotes())
            closeModal()
        } catch (rejection) {
            // Stays in the modal: nothing was added, and the button is the retry.
            addModalToast({
                id: 'save-error',
                msg: getRejectionMessage(rejection, 'Something went wrong while adding the promo'),
                icon: 'error',
                red: true,
            })
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <BaseModal
            closeOnOutSideClick={false}
            preventClose={isSaving}
            mobileFullscreen
            maxWidth={674}>
            <BaseModalHeader>
                <h2 className="text-xl font-bold">Add Lead Booster Promo</h2>
            </BaseModalHeader>
            <BaseModalMainContent>
                <InputField
                    name="nr_boosted_leads"
                    value={nrPromoLeads}
                    min={1}
                    legend
                    type="number"
                    error={error}
                    placeholder="Number of boosted leads"
                    onChange={(event) => {
                        const value = event.target.value
                        setNrPromoLeads(Number(value) || null)
                    }}
                />
                <Divider m />
                <InputField
                    newStyle
                    name="comment"
                    value={comment}
                    legend
                    textArea
                    maxLength={COMMENT_MAX_CHARACTERS}
                    placeholder="Comment (optional)"
                    onChange={(event) => setComment(event.target.value)}
                />
                <div>
                    {comment.length} / {COMMENT_MAX_CHARACTERS}
                </div>
                <Divider m />
                <Checkbox
                    name="force"
                    title="Force creation (override eligibility check)"
                    value={force}
                    onChange={() => setForce(!force)}
                />
            </BaseModalMainContent>
            <BaseModalFooter className="flex flex-wrap justify-between gap-2">
                <Button name="link" onClick={closeModal} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={onClickSave} disabled={isSaving}>
                    {isSaving ? <LoadingDots /> : 'Save'}
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default AddLeadBoosterPromoLeadsModal
